import { Button, DatePicker, Input, Modal, Select, Space, Table, TableProps, Tabs, Tag, message } from 'antd'
import { Dayjs } from 'dayjs'
import { useCallback, useEffect, useState } from 'react'

import { errorText, listContracts, voidContract } from '~/services/teacher-contract'
import { ITeacherContractSummary, TeacherContractStatus } from '~/types/teacher-contract'

import ContractDetailDrawer from './ContractDetailDrawer'
import { LANGUAGE_LABEL, STATUS_COLOR, STATUS_LABEL, fmt } from './constants'
import SendContractModal from './SendContractModal'
import TemplatePanel from './TemplatePanel'

const ContractList = () => {
  const [items, setItems] = useState<ITeacherContractSummary[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(20)
  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState<TeacherContractStatus | undefined>()
  const [keyword, setKeyword] = useState('')
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null)
  const [sendOpen, setSendOpen] = useState(false)
  const [detailId, setDetailId] = useState<number | null>(null)

  const fetchData = useCallback(() => {
    setLoading(true)
    listContracts({
      page,
      size,
      status,
      keyword: keyword.trim() || undefined,
      from: range?.[0]?.startOf('day').valueOf(),
      to: range?.[1]?.add(1, 'day').startOf('day').valueOf(),
    })
      .then((res) => {
        setItems(res.data.items)
        setTotal(res.data.total)
      })
      .catch(async (err) => message.error(await errorText(err, '加载失败，请稍后再试')))
      .finally(() => setLoading(false))
  }, [page, size, status, keyword, range])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const onVoid = (row: ITeacherContractSummary) => {
    let reason = ''
    Modal.confirm({
      title: `作废合同 ${row.contractNo}？`,
      content: (
        <Input.TextArea
          rows={3}
          placeholder="作废原因（必填，会记入审计记录）"
          onChange={(e) => (reason = e.target.value)}
        />
      ),
      okText: '作废',
      okButtonProps: { danger: true },
      onOk: async () => {
        if (!reason.trim()) {
          message.warning('请填写作废原因')
          throw new Error('reason required')
        }
        try {
          await voidContract(row.id, reason.trim())
          message.success('已作废')
          fetchData()
        } catch (err) {
          message.error(await errorText(err))
          throw err
        }
      },
    })
  }

  const columns: TableProps<ITeacherContractSummary>['columns'] = [
    { title: '合同编号', dataIndex: 'contractNo', width: 130 },
    {
      title: '老师',
      key: 'teacher',
      render: (_, r) => (
        <div>
          <div>{r.teacherName}</div>
          <div style={{ color: '#999', fontSize: 12 }}>{r.teacherEmail}</div>
        </div>
      ),
    },
    {
      title: '合同标题',
      key: 'title',
      render: (_, r) => (
        <span>
          <Tag>{LANGUAGE_LABEL[r.language]}</Tag>
          {r.title}
        </span>
      ),
    },
    { title: '报酬', dataIndex: 'feeSummary', render: (v) => v || '-' },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (v: TeacherContractStatus) => <Tag color={STATUS_COLOR[v]}>{STATUS_LABEL[v]}</Tag>,
    },
    { title: '发送时间', dataIndex: 'sentAt', width: 140, render: fmt },
    { title: '老师已读', dataIndex: 'viewedAt', width: 140, render: fmt },
    { title: '签署时间', dataIndex: 'signedAt', width: 140, render: fmt },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      render: (_, r) => (
        <Space>
          <a onClick={() => setDetailId(r.id)}>查看</a>
          {r.status === 'SENT' && (
            <a style={{ color: '#ff4d4f' }} onClick={() => onVoid(r)}>
              作废
            </a>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div>
      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          allowClear
          placeholder="老师姓名 / 邮箱 / 标题 / 报酬 / 合同编号"
          style={{ width: 300 }}
          onSearch={(v) => {
            setPage(1)
            setKeyword(v)
          }}
        />
        <Select
          allowClear
          placeholder="状态"
          style={{ width: 120 }}
          value={status}
          onChange={(v) => {
            setPage(1)
            setStatus(v)
          }}
          options={(Object.keys(STATUS_LABEL) as TeacherContractStatus[]).map((s) => ({
            value: s,
            label: STATUS_LABEL[s],
          }))}
        />
        <DatePicker.RangePicker
          placeholder={['发送开始日', '发送结束日']}
          value={range}
          onChange={(v) => {
            setPage(1)
            setRange(v)
          }}
        />
        <Button type="primary" onClick={() => setSendOpen(true)}>
          发送合同
        </Button>
      </Space>
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={items}
        pagination={{
          current: page,
          pageSize: size,
          total,
          showSizeChanger: true,
          onChange: (p, s) => {
            setPage(p)
            setSize(s)
          },
        }}
      />
      <SendContractModal
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        onSent={() => {
          setSendOpen(false)
          setPage(1)
          fetchData()
        }}
      />
      <ContractDetailDrawer id={detailId} onClose={() => setDetailId(null)} />
    </div>
  )
}

const TeacherContractPage = () => (
  <div style={{ padding: 24 }}>
    <Tabs
      items={[
        { key: 'contracts', label: '合同列表', children: <ContractList /> },
        { key: 'templates', label: '合同模板', children: <TemplatePanel /> },
      ]}
    />
  </div>
)

export default TeacherContractPage
